/**
 * admin/_primitives.jsx — Wave 6 Batch 2 DRY consolidation.
 *
 * Consolidates the 7 local primitives that were duplicated across
 * ~27 admin surfaces (Beta / Marketplace / Analytics / Credit Engine
 * anchors + propagation). Every consumer now imports these exports
 * instead of shipping a local copy.
 *
 * Design invariants preserved (from the anchor patterns approved
 * across Wave 5-Late):
 *   • `--ds-color-admin-border` hairline chrome + `box-shadow:
 *     0 1px 0 0 admin-border` on Section shells.
 *   • `--ds-radius-input` (3px) on inputs / selects; `--ds-radius-field`
 *     (4px) on buttons — intentional 1px hierarchy.
 *   • Dense `px-2.5 py-1.5` on inputs; `px-3 py-1.5` on buttons.
 *   • `text-[26px] tabular-nums font-semibold` on Stat values;
 *     `--ds-color-accent-strong` when `highlight`.
 *   • SegmentedFilter active state uses `--ds-color-accent-soft` bg +
 *     `--ds-color-accent-strong` ink.
 *
 * Section absorbs three optional props (`mb`, `headerPy`, `bodyClassName`)
 * so the BetaCodes anchor's looser spacing variant can opt in without
 * shipping a second component.
 *
 * NOT for consumer / member-facing surfaces (see Landing / Readiness /
 * legal pages — those use different chrome).
 */
import { Heading } from "@/design-system/components/Heading";

/* ------------------------------------------------------------------ */
/* Section                                                              */
/* ------------------------------------------------------------------ */

export function Section({
  title,
  icon: Icon,
  badge,
  testid,
  mb = "mb-3",
  headerPy = "py-2.5",
  bodyClassName = "p-3",
  children,
}) {
  return (
    <section
      data-testid={testid}
      className={`${mb} rounded-[var(--ds-radius-card)]`}
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-admin-border)",
        boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
      }}
    >
      <div
        className={`px-4 ${headerPy} border-b flex items-center justify-between gap-3 flex-wrap`}
        style={{ borderColor: "var(--ds-color-admin-border)" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {Icon && (
            <Icon
              className="w-4 h-4"
              style={{ color: "var(--ds-color-accent)" }}
              aria-hidden="true"
            />
          )}
          <Heading level={3} size="sm" weight="semibold" className="leading-none truncate">
            {title}
          </Heading>
        </div>
        {badge}
      </div>
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Stat                                                                 */
/* ------------------------------------------------------------------ */

export function Stat({ label, value, testid, highlight, icon: Icon, sub }) {
  return (
    <div
      className="rounded-[var(--ds-radius-card)] p-3"
      data-testid={testid}
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-admin-border)",
        boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
      }}
    >
      <div
        className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {Icon && (
          <Icon
            className="w-3 h-3"
            style={{ color: "var(--ds-color-accent)" }}
            aria-hidden="true"
          />
        )}
        {label}
      </div>
      <div
        className="mt-1.5 text-[26px] leading-tight tabular-nums font-semibold"
        style={{ color: highlight ? "var(--ds-color-accent-strong)" : "var(--ds-color-ink)" }}
      >
        {value}
      </div>
      {sub && (
        <div
          className="text-[10px] mt-1 tabular-nums"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SelectInput                                                          */
/* ------------------------------------------------------------------ */

export function SelectInput(props) {
  return (
    <select
      {...props}
      className={
        "rounded-[var(--ds-radius-input)] border bg-[var(--ds-color-surface)] " +
        "px-2.5 py-1.5 text-[12px] text-[var(--ds-color-ink)] " +
        "focus:outline-none focus:border-[var(--ds-color-input-border-strong)] " +
        (props.className || "")
      }
      style={{ borderColor: "var(--ds-color-input-border)", ...props.style }}
    />
  );
}

/* ------------------------------------------------------------------ */
/* TextInput                                                            */
/* ------------------------------------------------------------------ */

export function TextInput(props) {
  return (
    <input
      {...props}
      className={
        "rounded-[var(--ds-radius-input)] border bg-[var(--ds-color-surface)] " +
        "px-2.5 py-1.5 text-[13px] text-[var(--ds-color-ink)] " +
        "placeholder:text-[var(--ds-color-ink-subtle)] " +
        "focus:outline-none focus:border-[var(--ds-color-input-border-strong)] " +
        (props.className || "")
      }
      style={{ borderColor: "var(--ds-color-input-border)", ...props.style }}
    />
  );
}

/* ------------------------------------------------------------------ */
/* TextArea                                                             */
/* ------------------------------------------------------------------ */

export function TextArea(props) {
  return (
    <textarea
      {...props}
      className={
        "rounded-[var(--ds-radius-input)] border bg-[var(--ds-color-surface)] " +
        "px-2.5 py-1.5 text-[13px] text-[var(--ds-color-ink)] " +
        "placeholder:text-[var(--ds-color-ink-subtle)] " +
        "focus:outline-none focus:border-[var(--ds-color-input-border-strong)] " +
        (props.className || "")
      }
      style={{
        borderColor: "var(--ds-color-input-border)",
        resize: "vertical",
        ...props.style,
      }}
    />
  );
}

/* ------------------------------------------------------------------ */
/* GhostButton                                                          */
/* ------------------------------------------------------------------ */

export function GhostButton({ children, className = "", ...rest }) {
  return (
    <button
      type="button"
      {...rest}
      className={
        "inline-flex items-center gap-1.5 rounded-[var(--ds-radius-field)] border " +
        "border-[var(--ds-color-admin-border)] px-3 py-1.5 text-[12px] " +
        "text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] " +
        "disabled:opacity-50 transition-colors " +
        className
      }
      style={{ transitionDuration: "var(--ds-duration-fast)", ...rest.style }}
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* AccentButton                                                         */
/* ------------------------------------------------------------------ */

export function AccentButton({ children, className = "", ...rest }) {
  return (
    <button
      type="button"
      {...rest}
      className={
        "inline-flex items-center gap-1.5 rounded-[var(--ds-radius-field)] px-3 py-1.5 " +
        "text-[12px] font-semibold uppercase tracking-[0.08em] disabled:opacity-60 transition-colors " +
        className
      }
      style={{
        background: "var(--ds-color-accent)",
        color: "var(--ds-color-accent-ink)",
        transitionDuration: "var(--ds-duration-fast)",
        ...rest.style,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ds-color-accent-strong)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "var(--ds-color-accent)")}
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* SegmentedFilter                                                      */
/* ------------------------------------------------------------------ */

export function SegmentedFilter({ options, value, onChange, testIdPrefix }) {
  return (
    <div
      className="inline-flex rounded-[var(--ds-radius-input)] overflow-hidden"
      style={{ border: "1px solid var(--ds-color-admin-border)" }}
    >
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            data-testid={`${testIdPrefix}-${opt.value}`}
            className={
              "px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] font-semibold transition-colors " +
              (active ? "" : "hover:text-[var(--ds-color-ink)]")
            }
            style={{
              background: active ? "var(--ds-color-accent-soft)" : "transparent",
              color: active ? "var(--ds-color-accent-strong)" : "var(--ds-color-ink-muted)",
              transitionDuration: "var(--ds-duration-fast)",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
